import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import {
  AppContext,
  DatePicker,
  RBAC,
  ErrorHandling,
  SelectSearch,
  assets,
  Input,
  form2,
  AuthService,
  downloadURI,
  DistributionServices
} from '@pulseops/common'
import { useIsFocused } from '@react-navigation/native'
import { StackScreenProps } from '@react-navigation/stack'
import { UtilitiesStackParamList } from '../utilites-stack-params-list'
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@material-ui/core'
import { Throwable, ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/lib/function'
import { Controller } from 'react-hook-form'
import moment from 'moment'
import _ from 'lodash'
import { CandidateInformationForm } from './CandidateInformationForm'
import { CandidateInformationConst } from './CandidateInformationConst'
import { CandidateInformationServices } from './CandidateInformationServices'
import { useLoading } from '@mxt/zio-react'

type props = StackScreenProps<UtilitiesStackParamList, 'CandidateInformationScreen'>

export const CandidateInformationScreen = ({ navigation }: props) => {
  const { t, i18n } = useTranslation()
  const ht = useTranslation('menu').t
  const formSaleStructure = form2.useForm(CandidateInformationForm.codec, {
    defaultValues: {
      fromDate: new Date(),
      toDate: new Date(),
      template: {
        value: CandidateInformationConst.Extension.EXCEL,
        label: 'Excel'
      }
    }
  })
  const { base, handleSubmit } = formSaleStructure
  const [maxDate] = React.useState<Date>(new Date())
  const [dataSource, setDataSource] = React.useState<CandidateInformationServices.DataList[]>([])
  const isFocused = useIsFocused()
  // const [loading, bindLoader] = useLoading(false)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [totalItem, setTotalItem] = React.useState<number>(0)
  const { changeBreadcrumb, showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const [messageResponse, setMessageResponse] = React.useState<string | null>(null)
  const [submittedData, setSubmittedData] = React.useState<any>({})
  const permissions: string[] = pipe(RBAC.permissions, ErrorHandling.runDidMount([]))
  const [loading, bindLoading] = useLoading(false)
  const formError = _.get(base.formState.errors, '.message')

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            navigation.navigate('Utilities')
          }
        },
        {
          title: ht('DistributionServices'),
          navigate: () => {
            navigation.navigate('DistributionServicesScreen')
          }
        },
        {
          title: t('Reports:CandidateInfo'),
          navigate: null
        }
      ])
    }
  }, [isFocused])

  React.useEffect(() => {
    showGlobalLoading(loading)
  }, [loading])

  const transTypeDAOptions = React.useMemo(
    () => CandidateInformationConst.TransactionList.map((item) => ({ ...item, label: t(item.label) })),
    []
  )

  const onViewReportClick = handleSubmit((validated) => {
    setSubmittedData(validated)
    handleData(validated, pageNum, pageSize)
  })

  const handleData = (submitData: any, page: number, size: number) => {
    let data = {
      fromDate: submitData.fromDate ? moment(submitData.fromDate).format('DD/MM/yyyy') : '',
      toDate: submitData.toDate ? moment(submitData.toDate).format('DD/MM/yyyy') : '',
      saleType: submitData.saleType?.value ?? 'All',
      template: submitData.template?.value ?? 'All',
      source: submitData.source?.value ?? 'All',
      email: submitData.email ?? 'All',
      transactionType: submitData.transactionType?.value ?? 'All',
      status: submitData.status?.value ?? 'All',
      category: 'All',
      pageSize: size,
      pageNum: page
    }
    pipe(
      CandidateInformationServices.getReport(data),
      ZIO.tap((res) => {
        console.log(res.body?.elements, res.body?.elements?.length, 'res.body?.elements?.length')
        setTotalItem(res.body?.totalElement ?? 0)
        setMessageResponse(res.body?.totalElement === 0 ? t('message:MS030029') : null)
        return ZIO.unit
      }),
      ZIO.tap((res) => {
        console.log(res.body?.totalElement, 'dataSourcedataSourcedataSource')
        setDataSource(res.body?.elements ?? [])
        return ZIO.unit
      }),
      bindLoading,
      ZIO.unsafeRun({})
    )
  }

  // export data to exceljs

  const onExcelExport = handleSubmit((validated) => {
    let data = {
      fromDate: validated.fromDate ? moment(validated.fromDate).format('DD/MM/yyyy') : '',
      toDate: validated.toDate ? moment(validated.toDate).format('DD/MM/yyyy') : '',
      saleType: validated.saleType?.value ?? 'All',
      template: validated.template?.value ?? 'All',
      source: validated.source?.value ?? 'All',
      email: validated.email ?? 'All',
      transactionType: validated.transactionType?.value ?? 'All',
      status: validated.status?.value ?? 'All',
      category: 'All'
    }
    pipe(
      ZIO.zipPar(AuthService.token, AuthService.getLoginType, CandidateInformationServices.getReportData(data)),
      ZIO.flatMap(([token, loginType, res]) => {
        return !!res && !!res.body
          ? pipe(
              ZIO.fromPromise(() =>
                fetch('data:application/vnd.openxmlformats-officedocument.speadsheetml.sheet;base64,' + res.body, {
                  method: 'GET',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'X-Authen-Vendor': loginType
                  }
                })
              ),
              ZIO.flatMap((res) =>
                ZIO.zipPar(
                  ZIO.succeed(res),
                  ZIO.fromPromise(() => res.blob())
                )
              ),
              ZIO.map(([res, blob]) => {
                return downloadURI(window.URL.createObjectURL(blob), `Report_Candidate_Information_result_export.xlsx`)
              })
            )
          : ZIO.succeed(showToast(t(`message:MS030029`), 'error'))
      }),
      ZIO.unsafeRun({})
    )
  })

  return (
    <View style={SUREStyle.pageContent}>
      <ScrollView style={[SUREStyle.container]}>
        <View style={SUREStyle.sectionContent}>
          <View style={SUREStyle.sectionRow}>
            <View style={SUREStyle.col_4}>
              <Controller
                name="fromDate"
                control={base.control}
                rules={{
                  required: {
                    value: true,
                    message: `${t('message:MS020009', { field: t('common:SubmissionDateFrom') })}`
                  }
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <DatePicker
                    label={t('common:SubmissionDateFrom')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={!!value ? '' : error?.message}
                    maxDate={maxDate}
                    maxDateMessage={'Date should not be future'}
                    required
                  />
                )}
              />
            </View>
            <View style={SUREStyle.col_4}>
              <Controller
                name="toDate"
                control={base.control}
                rules={{
                  required: {
                    value: true,
                    message: `${t('message:MS020009', { field: t('common:SubmissionDateTo') })}`
                  }
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <DatePicker
                    label={t('common:SubmissionDateTo')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={!!value ? '' : error?.message}
                    maxDate={maxDate}
                    maxDateMessage={'Date should not be future'}
                    required
                  />
                )}
              />
            </View>
            <View style={[SUREStyle.col_4, { marginTop: 6 }]}>
              <Controller
                name="email"
                control={base.control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    title={t('Reports:Email')}
                    value={value ?? undefined}
                    onChange={onChange}
                    errorMessage={error?.message}
                  />
                )}
              />
            </View>
            <View style={[SUREStyle.col_4, { marginTop: 6 }]}>
              <Controller
                name="saleType"
                control={base.control}
                render={({ field }) => (
                  <SelectSearch
                    {...field}
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    label={t('Reports:SaleType')}
                    options={CandidateInformationConst.SaleTypeList}
                    placeholder={t('common:Select')}
                  />
                )}
              />
            </View>
          </View>

          <View style={[SUREStyle.sectionRow, SUREStyle.secondLine]}>
            {/* <View style={SUREStyle.col_4}>
              <Controller
                name="status"
                control={base.control}
                render={({ field }) => (
                  <SelectSearch
                    {...field}
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    label={t('Reports:Status')}
                    options={CandidateInformationConst.StatusList.map((item) => ({
                      label: t(`Reports:${item.label}`),
                      value: item.value
                    }))}
                    placeholder={t('common:Select')}
                  />
                )}
              />
            </View> */}

            <View style={SUREStyle.col_4}>
              <Controller
                name="template"
                control={base.control}
                render={({ field }) => (
                  <SelectSearch
                    {...field}
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    label={t('Reports:Template')}
                    options={CandidateInformationConst.TemplateList}
                    placeholder={t('common:Select')}
                  />
                )}
              />
            </View>
            <View style={SUREStyle.col_4}>
              <Controller
                name="source"
                control={base.control}
                render={({ field }) => (
                  <SelectSearch
                    {...field}
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    label={t('Reports:Source')}
                    options={CandidateInformationConst.sourceDAOptions}
                    placeholder={t('common:Select')}
                  />
                )}
              />
            </View>
            <View style={SUREStyle.col_4}>
              <Controller
                name="transactionType"
                control={base.control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <SelectSearch
                    label={t('ServiceInquiry:TransactionType')}
                    options={transTypeDAOptions}
                    placeholder={t('common:Select')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value ?? undefined}
                  />
                )}
              />
            </View>
          </View>

          <View style={[SUREStyle.sectionRow, SUREStyle.secondLine]}>
            <View style={[SUREStyle.col_4, { flex: 1, flexDirection: 'row', alignItems: 'center' }]}>
              <TouchableOpacity style={{ width: 118, height: 40 }} onPress={onViewReportClick}>
                <View style={SUREStyle.primaryBtn}>
                  <Text style={{ textAlign: 'center', color: '#fff' }}>{t('Reports:View')}</Text>
                </View>
              </TouchableOpacity>

              {permissions.includes(DistributionServices.ExportCandidateInforDistributionServices) && (
                <TouchableOpacity style={{ width: 118, height: 40, marginLeft: 30 }} onPress={onExcelExport}>
                  <View style={SUREStyle.primaryBtn}>
                    <Text style={{ textAlign: 'center', color: '#fff' }}>{t('Reports:Export')}</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>
          </View>
          {formError && <Text style={{ color: '#ed1b2e', marginVertical: 12 }}>{formError}</Text>}
        </View>
        {loading && <LinearProgress color="secondary" />}
        {messageResponse ? (
          <View style={{ marginHorizontal: 30 }}>
            <Text style={{ color: '#ED1B2E' }}>{messageResponse}</Text>
          </View>
        ) : (
          dataSource &&
          dataSource.length > 0 && (
            <View style={{ marginTop: 20, marginBottom: 20 }}>
              <TableContainer component={Paper} style={{ maxHeight: 350 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#e2e7ea' }}>
                      <TableCell align="center" style={{ fontWeight: 'bold', fontSize: 15 }}>
                        #
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 110, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:eREF')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 180, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:SubChannel')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:SubmissionDate')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:LastName')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:FirstName')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:DOB')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Gender')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:GenderCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:CitizenID')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 120, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:IDDate')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 130, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:IDPlace')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 220, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:ResidentialAddress')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 220, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:LocationCodeResidential')}
                      </TableCell>

                      <TableCell align="left" style={{ minWidth: 110, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:ResidentialDescription')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 180, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:ContactAddress')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:LocationCodeContact')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:ContactAddressDescription')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:AllocatedAddress')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:LocationCodeAllocated')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:AllocatedAddressDescription')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Email')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:PhoneNumber')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 120, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:PlaceOfBirth')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 130, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Zalo')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 220, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:OccupationCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 220, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Occupation')}
                      </TableCell>

                      {/* <TableCell align="left" style={{ minWidth: 110, fontWeight: 'bold', fontSize: 15 }}>
                      {t('Reports:ResidentialDescription')}
                    </TableCell> */}
                      <TableCell align="left" style={{ minWidth: 180, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Education')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:EducationCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:MarriedStatus')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:MarriedStatusCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Income')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:LeaderCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:LeaderCodeDesc')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:BankCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 120, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:BankName')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 130, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:BranchName')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 220, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:BankNameOther')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 220, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:BranchNameOther')}
                      </TableCell>
                      {/* <TableCell align="left" style={{ minWidth: 110, fontWeight: 'bold', fontSize: 15 }}>
                      {t('Reports:ResidentialDescription')}
                    </TableCell> */}
                      <TableCell align="left" style={{ minWidth: 180, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:BankAccount')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:TaxCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:AgentCodeRefer')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:AgentNameRefer')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:PotentialCustomer')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Ethnic')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Specialization')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:NumberOfChildren')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:SaleZone')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:OfficeCode')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:SaleType')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 200, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:AgentCategory')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 120, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Note')}
                      </TableCell>
                      {/* <TableCell align="left" style={{ minWidth: 120, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:Status')}
                      </TableCell> */}
                      <TableCell align="left" style={{ minWidth: 130, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:OldIDNo')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 130, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:IDExpiredDate')}
                      </TableCell>
                      <TableCell align="left" style={{ minWidth: 130, fontWeight: 'bold', fontSize: 15 }}>
                        {t('Reports:OCR')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataSource.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell align="center">{++i}</TableCell>
                        <TableCell align="left">{row.eref ?? '-'}</TableCell>
                        <TableCell align="left">{row.applicationInfoView?.subChanel ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.applicationInfoView?.submissionDate ? row.applicationInfoView?.submissionDate : '-'}
                        </TableCell>
                        <TableCell align="left">{row.candidateInfoView?.lastName ?? '-'}</TableCell>
                        <TableCell align="left">{row.candidateInfoView?.firstName ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.candidateInfoView?.dateOfBirth ? row.candidateInfoView?.dateOfBirth : '-'}
                        </TableCell>
                        <TableCell align="left">{row.candidateInfoView?.gender?.label ?? '-'}</TableCell>
                        <TableCell align="left">{row.candidateInfoView?.gender?.value ?? '-'}</TableCell>
                        <TableCell align="left">{row.candidateInfoView?.idNo ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.otherInfoView?.idDate ? row.otherInfoView?.idDate : '-'}
                        </TableCell>
                        <TableCell align="left">{row.otherInfoView?.idPlace ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.contactInfoView?.streetResidential ??
                            // + ' ' +
                            // row.contactInfoView?.wardResidential?.label +
                            // ' ' +
                            // row.contactInfoView?.districtResidential?.label +
                            // ' ' +
                            // row.contactInfoView?.provinceResidential?.label
                            '-'}
                        </TableCell>
                        <TableCell align="left">{row.contactInfoView?.locationCodeResidential}</TableCell>
                        <TableCell align="left">
                          {row.contactInfoView?.streetResidential +
                            ' ' +
                            row.contactInfoView?.wardResidential?.label +
                            ' ' +
                            row.contactInfoView?.districtResidential?.label +
                            ' ' +
                            row.contactInfoView?.provinceResidential?.label ?? '-'}
                        </TableCell>
                        <TableCell align="left">
                          {row.contactInfoView?.streetContact ??
                            // + ' ' +
                            // row.contactInfoView?.wardContact?.label +
                            // ' ' +
                            // row.contactInfoView?.districtContact?.label +
                            // ' ' +
                            // row.contactInfoView?.provinceContact?.label
                            '-'}
                        </TableCell>
                        <TableCell align="left">{row.contactInfoView?.locationCodeContact ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.contactInfoView?.streetContact +
                            ' ' +
                            row.contactInfoView?.wardContact?.label +
                            ' ' +
                            row.contactInfoView?.districtContact?.label +
                            ' ' +
                            row.contactInfoView?.provinceContact?.label ?? '-'}
                        </TableCell>
                        <TableCell align="left">
                          {row.contactInfoView?.streetAllocated ??
                            // + ' ' +
                            // row.contactInfoView?.wardAllocated?.label +
                            // ' ' +
                            // row.contactInfoView?.districtAllocated?.label +
                            // ' ' +
                            // row.contactInfoView?.provinceAllocated?.label
                            '-'}
                        </TableCell>
                        <TableCell align="left">{row.contactInfoView?.locationCodeAllocated ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.contactInfoView?.streetAllocated +
                            ' ' +
                            row.contactInfoView?.wardAllocated?.label +
                            ' ' +
                            row.contactInfoView?.districtAllocated?.label +
                            ' ' +
                            row.contactInfoView?.provinceAllocated?.label ?? '-'}
                        </TableCell>
                        <TableCell align="left">{row.contactInfoView?.email ?? '-'}</TableCell>
                        <TableCell align="left">{row.contactInfoView?.phoneNumber ?? '-'}</TableCell>
                        <TableCell align="left">{row.otherInfoView?.placeOfBirth ?? '-'}</TableCell>
                        <TableCell align="left">{row.contactInfoView?.zalo ?? '-'}</TableCell>
                        <TableCell align="left">{row.educationInfoView?.occupation?.value ?? '-'}</TableCell>
                        <TableCell align="left">{row.educationInfoView?.occupation?.label ?? '-'}</TableCell>
                        <TableCell align="left">{row.educationInfoView?.education?.value ?? '-'}</TableCell>
                        <TableCell align="left">{row.educationInfoView?.education?.label ?? '-'}</TableCell>
                        <TableCell align="left">{row.maritalStatusView?.maritalStatus?.label ?? '-'}</TableCell>
                        <TableCell align="left">{row.maritalStatusView?.maritalStatus?.value ?? '-'}</TableCell>
                        <TableCell align="left">{row.educationInfoView?.income?.label ?? '-'}</TableCell>
                        <TableCell align="left">{row.applicationInfoView?.leaderCode ?? '-'}</TableCell>
                        <TableCell align="left">{row.applicationInfoView?.leaderCodeDescription ?? '-'}</TableCell>
                        <TableCell align="left">{row.bankAccountInfoView?.bankCode ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.bankAccountInfoView?.bankName ? row.bankAccountInfoView?.bankName.label : '-'}
                        </TableCell>
                        <TableCell align="left">
                          {row.bankAccountInfoView?.bankBranchName
                            ? row.bankAccountInfoView?.bankBranchName.label
                            : '-'}
                        </TableCell>
                        <TableCell align="left">{row.bankAccountInfoView?.bankNameOther ?? '-'}</TableCell>
                        <TableCell align="left">{row.bankAccountInfoView?.bankBranchNameOther ?? '-'}</TableCell>
                        <TableCell align="left">{row.bankAccountInfoView?.bankAccount ?? '-'}</TableCell>
                        <TableCell align="left">{row.educationInfoView?.taxCode ?? '-'}</TableCell>
                        <TableCell align="left">{row.otherInfoView?.agentCodeRefer ?? '-'}</TableCell>
                        <TableCell align="left">{row.otherInfoView?.agentNameRefer ?? '-'}</TableCell>
                        <TableCell align="left">{row.otherInfoView?.potentialCustomer ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.otherInfoView?.ethNic !== '' ? row.otherInfoView?.ethNic : '-'}
                        </TableCell>
                        <TableCell align="left">
                          {row.educationInfoView?.specialization !== '' ? row.educationInfoView?.specialization : '-'}
                        </TableCell>
                        <TableCell align="left">{row.maritalStatusView?.numberOfChildren ?? '-'}</TableCell>
                        <TableCell align="left">{row.applicationInfoView?.saleZone ?? '-'}</TableCell>
                        <TableCell align="left">{row.applicationInfoView?.officeCode ?? '-'}</TableCell>
                        <TableCell align="left">{row.saleType ?? '-'}</TableCell>
                        <TableCell align="left">{row.agentCategory ?? '-'}</TableCell>
                        <TableCell align="left">
                          {row.otherInfoView?.note !== '' ? row.otherInfoView?.note : '-'}
                        </TableCell>
                        {/* <TableCell align="left">{row.status !== '' ? row.status : '-'}</TableCell> */}
                        <TableCell align="left">
                          {row.candidateInfoView?.oldIDNo !== '' ? row.candidateInfoView?.oldIDNo : '-'}
                        </TableCell>
                        <TableCell align="left">
                          {row.otherInfoView?.idExpiredDate ? row.otherInfoView?.idExpiredDate : '-'}
                        </TableCell>
                        <TableCell align="left">{row.applicationInfoView?.ocr ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                page={pageNum}
                rowsPerPageOptions={[5, 10, 25]}
                rowsPerPage={pageSize}
                count={totalItem}
                onPageChange={(e, page) => {
                  setPageNum(page)
                  handleData(submittedData, page, pageSize)
                }}
                onRowsPerPageChange={(e) => {
                  const rowsPerPage = Number(e.target.value)
                  setPageSize(rowsPerPage)
                  if (pageNum * rowsPerPage >= totalItem) {
                    setPageNum(0)
                    handleData(submittedData, 0, rowsPerPage)
                  } else {
                    handleData(submittedData, pageNum, rowsPerPage)
                  }
                }}
                labelRowsPerPage={t('common:PaginationSize')}
                labelDisplayedRows={({ from, to, count }) => t('common:Pagination', { from, to, count })}
                component={View}
              ></TablePagination>
            </View>
          )
        )}
      </ScrollView>
    </View>
  )
}

const SUREStyle = StyleSheet.create({
  pageContent: {
    backgroundColor: '#fff',
    height: '100%',
    width: '100%'
  },
  container: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    paddingLeft: 15,
    paddingRight: 15
  },
  sectionContent: {
    padding: 20,
    backgroundColor: '#fafafa',
    border: '1px solid #d3dce6',
    boxSizing: 'border-box',
    borderRadius: 8,
    marginTop: 30
  },
  sectionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    marginRight: -15,
    marginLeft: -15
  },
  secondLine: {
    marginTop: 20
  },
  col_4: {
    width: '100%',
    maxWidth: '22%',
    paddingRight: 15,
    paddingLeft: 15
  },
  primaryBtn: {
    width: 118,
    height: 40,
    backgroundColor: '#ed1b2e',
    borderRadius: 100,
    padding: 10,
    boxShadow: 'none'
  },
  cellWidth200: {
    minWidth: '200px'
  }
})
